import React, { useContext, useMemo } from 'react';
import { Box, Paper, Typography, Avatar, Chip, Stack } from '@mui/material';
import { Link } from 'react-router-dom';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import { DateTime } from 'luxon';
import { CacheContext, CacheContextType } from '../context/CacheContext';
import TimeDuration from '../TimeDuration';

/**
 * NEARLY DEAD
 *
 * Discovery widget for the sidebar that surfaces posts expiring in
 * the next hour, ranked by engagement (vote count + reply count). The
 * FOMO engine — "you have 7 minutes to see this post that 40 people
 * voted on, then it's gone forever".
 *
 * The ranking combines time-to-death (closer = higher) with
 * engagement (more = higher). Excludes sparks (they already have
 * their own lifespan) and permanent posts.
 */

const LOOKAHEAD_SECONDS = 3600; // within the next hour

function NearlyDead() {
  const { allPosts, getUserRecord } = useContext<CacheContextType>(CacheContext);

  const candidates = useMemo(() => {
    const now = DateTime.now();
    return allPosts()
      .filter((p) => {
        if (p.is_permanent || p.is_expired || p.is_spark) return false;
        if (p.parent_id) return false; // only top-level
        const expires = DateTime.fromISO(p.expire_at);
        if (!expires.isValid) return false;
        const remaining = expires.diff(now, 'seconds').seconds;
        return remaining > 0 && remaining <= LOOKAHEAD_SECONDS;
      })
      .map((p) => {
        const engagement = (p.vote_count ?? 0) * 2 + (p.reply_count ?? 0);
        return { post: p, engagement };
      })
      .sort((a, b) => {
        // Primary: engagement desc; tiebreak: closer to death first
        if (b.engagement !== a.engagement) return b.engagement - a.engagement;
        return (
          DateTime.fromISO(a.post.expire_at).toSeconds() -
          DateTime.fromISO(b.post.expire_at).toSeconds()
        );
      })
      .slice(0, 3);
  }, [allPosts]); // eslint-disable-line react-hooks/exhaustive-deps

  if (candidates.length === 0) return null;

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        borderLeft: '3px solid #b51e1b',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontWeight: 700,
          color: '#b51e1b',
        }}
      >
        <WhatshotIcon sx={{ width: 16, height: 16 }} />
        Nearly Dead
      </Typography>
      <Stack spacing={1}>
        {candidates.map(({ post }) => {
          const author = getUserRecord(post.user_record_id);
          const expiresInSec =
            DateTime.fromISO(post.expire_at).toSeconds() - DateTime.now().toSeconds();
          const critical = expiresInSec < 300; // under 5 min
          const preview = (post.value ?? '')
            .replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1')
            .replace(/#\[([^\]]+)\]\([^)]+\)/g, '#$1')
            .slice(0, 80);
          return (
            <Box
              key={post.id}
              component={Link}
              to={`/posts/${post.id}`}
              sx={{
                display: 'block',
                textDecoration: 'none',
                color: 'inherit',
                p: 0.85,
                borderRadius: 1,
                bgcolor: critical ? 'rgba(181,30,27,0.06)' : 'transparent',
                border: '1px solid',
                borderColor: critical ? 'rgba(181,30,27,0.3)' : 'divider',
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: critical ? 'rgba(181,30,27,0.12)' : 'action.hover',
                  borderColor: '#b51e1b',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                <Avatar
                  src={author?.avatar_url ?? undefined}
                  sx={{ width: 20, height: 20, fontSize: '0.6rem', bgcolor: 'primary.main' }}
                >
                  {author?.username?.charAt(0).toUpperCase() ?? '?'}
                </Avatar>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', flex: 1, minWidth: 0 }} noWrap>
                  @{author?.username ?? '???'}
                </Typography>
                <Chip
                  size="small"
                  label={<TimeDuration jsDate={DateTime.fromISO(post.expire_at).toJSDate()} />}
                  sx={{
                    height: 16,
                    fontSize: '0.62rem',
                    bgcolor: critical ? 'rgba(181,30,27,0.18)' : 'rgba(255,152,0,0.14)',
                    color: critical ? '#b51e1b' : '#ff9800',
                    fontVariantNumeric: 'tabular-nums',
                    '& .MuiChip-label': { px: 0.6, fontWeight: 700 },
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  color: 'text.secondary',
                  fontSize: '0.72rem',
                  lineHeight: 1.3,
                }}
              >
                {preview}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}

export default NearlyDead;
